import React from 'react';
import { Link } from 'react-router-dom';
import { Building2 } from 'lucide-react';

import sslIcon from '../assets/images/trust/ssl-secured.jpeg';
import pdpaIcon from '../assets/images/trust/pdpa-protection.jpeg';
import lhdnIcon from '../assets/images/trust/lhdn-myinvois.jpeg';
import cloudIcon from '../assets/images/trust/cloud-infrastructure.jpeg';
import apiIcon from '../assets/images/trust/api-integration.jpeg';

const footerNav = [
  {
    title: 'StayMii\u2122',
    links: [
      { label: 'StayMii e-Invoice\u2122', href: '/einvoice/product' },
      { label: 'StayMii e-Ticket\u2122', href: '/e-ticket' },
      { label: 'StayMii HR\u2122 (Free)', href: '/staymii-hr' },
      { label: 'StayMii Care\u2122', href: '/staymii-care' },
      { label: 'StayMii Care Sign In', href: '/care/login' },
      { label: 'Get Started', href: '/get-started' },
      { label: 'Integrations', href: '/partners' },
      { label: 'Pricing', href: '/pricing' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Features', href: '/features' },
      { label: 'Developer Overview', href: '/developers' },
      { label: 'Blog', href: '/blog' },
    ],
  },
  {
    title: 'Legal & Trust',
    links: [
      { label: 'Terms of Service', href: '/terms-of-service' },
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Cookies Policy', href: '/cookies' },
      { label: 'Security & Compliance', href: '/security' },
      { label: 'Trust Center', href: '/trust' },
      { label: 'Intellectual Property', href: '/intellectual-property' },
      { label: 'Certified by MySTI', href: '/certified-by-mysti' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'Contact Us', href: '/contact' },
      { label: 'About Us', href: '/about' },
      { label: 'Press', href: '/press' },
    ],
  },
];

const trustItems = [
  { img: '/mysti-white.png', label: 'MySTI Certified', href: '/certified-by-mysti' },
  { img: sslIcon, label: 'SSL Secured' },
  { img: lhdnIcon, label: 'LHDN e-Invoice Ready' },
  { icon: Building2, label: 'SSM Registered Company' },
  { img: pdpaIcon, label: 'PDPA Compliant' },
  { img: cloudIcon, label: 'Secure Cloud Infrastructure' },
  { img: apiIcon, label: 'API Integration Ready' },
];

const LandingFooter = () => {
  return (
    <footer style={{ backgroundColor: '#192336' }} data-testid="landing-footer">

      {/* Brand */}
      <div className="max-w-6xl mx-auto px-4 pt-12">
        <Link to="/" className="inline-flex items-center" data-testid="footer-logo">
          <img src="/staymii-logo-horizontal-white.png" alt="StayMii" className="h-16 w-auto" />
        </Link>
      </div>

      {/* Navigation */}
      <div className="max-w-6xl mx-auto px-4 pt-8 pb-8">
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {footerNav.map((section) => (
            <div key={section.title}>
              <h4
                className="text-white text-xs font-semibold mb-4 uppercase tracking-widest"
                data-testid={`footer-heading-${section.title.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
              >
                {section.title}
              </h4>
              <ul className="space-y-2.5">
                {section.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-white/55 text-sm hover:text-white transition-colors"
                      data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
              {section.title === 'Company' && (
                <Link
                  to="/certified-by-mysti"
                  className="inline-flex mt-5 bg-white rounded-xl p-2 shadow-sm hover:opacity-90 transition-opacity"
                  data-testid="footer-mysti-logo"
                  aria-label="Certified by MySTI"
                >
                  <img src="/mysti-certified.png" alt="MySTI Certified" className="h-12 w-12 object-contain" />
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Trust Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <p
            className="text-center text-white/40 text-[11px] uppercase tracking-widest font-semibold mb-4"
            data-testid="trust-bar-title"
          >
            Trusted & Secure Platform
          </p>
          <div className="flex flex-wrap justify-center gap-x-9 gap-y-4" data-testid="trust-bar">
            {trustItems.map(({ img, icon: Icon, label, href }) => {
              const inner = (
                <>
                  {img ? (
                    <img src={img} alt={label} className="h-8 w-8 object-contain" style={{ mixBlendMode: 'screen' }} />
                  ) : (
                    <span className="h-8 w-8 flex items-center justify-center rounded-md bg-white/10 border border-white/20">
                      <Icon className="h-4 w-4 text-white/70" />
                    </span>
                  )}
                  <span className="text-white/50 text-xs text-center">{label}</span>
                </>
              );
              return href ? (
                <Link key={label} to={href} className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid={`trust-item-${label.toLowerCase().replace(/\s+/g, '-')}`}>
                  {inner}
                </Link>
              ) : (
                <div key={label} className="flex items-center gap-2" data-testid={`trust-item-${label.toLowerCase().replace(/\s+/g, '-')}`}>
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom — Copyright & IP */}
      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center space-y-2" data-testid="footer-bottom">
          <p className="text-white/60 text-[13px] font-medium">
            Powered by Mii Global | StayMii&trade; Platform
          </p>
          <p className="text-[#888] text-[13px] leading-relaxed">
            &copy; {new Date().getFullYear()} Mii Global Partner Holdings Sdn Bhd (202601028203 (1690298-K)). All rights reserved.
          </p>
          <p className="text-[#888] text-[12px] leading-loose">
            StayMii Cloud Hospitality Management Software is a registered copyrighted work in Malaysia. Application No: LY2026E01573
          </p>
          <p className="text-[#888] text-[11px] leading-relaxed pt-1" data-testid="footer-trademark-notice">
            Trademark Notice: StayMii&trade; is a trademark pending registration with the Intellectual Property Corporation of Malaysia (MyIPO).
          </p>
        </div>
      </div>
    </footer>
  );
};

export default LandingFooter;
